import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_INFO,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_WARNING,
} from 'ui-components/services/notification-service';
import { NotificationHubService } from '../notification-hub/notification-hub-service';
import { NotificationType } from '../notification-hub/notification-hub.types';

import * as Actions from './csv-import.actions';
import { csvImportFinishedSubject } from './csv-import.observable';
import { epicMessages as messages } from './messages';
import { ImportStatus } from '../../components/csv-import/data/types/import-status';
import { ImportType } from '../../components/csv-import/data/types/import-type';
import { createImportSearchPath, createImportSummarySearchPath } from '../../components/csv-import/ui/hooks/use-csv-import';
import { PusherService } from '../../services/pusher-service';
import { normalizePusherMessageData } from '../../services/pusher-service/normalize-pusher-message';
import { WorkflowSteps, trackWorkflow } from '../../tracking/use-workflow-tracking';

/**
 * @typedef {import('../../components/csv-import/data/types/import-type').ImportTypes} ImportTypes
 */

/**
 * @typedef {import("../../components/csv-import/data/types/types").RequestImportActionPayload} RequestImportActionPayload
 */

/**
 * @typedef {object} Summary
 * @property {string} jobId
 * @property {ImportTypes} importType
 * @property {boolean} success
 * @property {number} successCount
 * @property {number} failedCount
 * @property {number} warningsCount
 * @property {number} totalRecords
 */

/**
 * @param {object} message
 * @param {RequestImportActionPayload} payload
 * @returns {Summary}
 */
const extractSummaryDetails = (message, payload) => {
  const data = normalizePusherMessageData(message?.data);
  const summary = data?.Summary;
  return {
    jobId: data?.JobId,
    // we could also get importType from message
    // but it may differ in case of letters, so instead of checking the case in every place, simply use import type from the payload.
    importType: payload.importType,
    success: data?.Success,

    successCount: summary?.Processed || 0,
    failedCount: summary?.FailedCount || 0,
    warningsCount: summary?.WarningsCount || 0,
    totalRecords: summary?.TotalRecords || 0,
  };
};

/**
 * @param {{history: object, onViewSummaryClick: () => void, summary: Summary}} props
 * @returns {JSX.Element}
 */
const SuccessfulImportContent = ({ history, onViewSummaryClick, summary }) => {
  const hasErrors = summary.failedCount > 0;
  const hasWarnings = summary.warningsCount > 0;
  const supportedSummaryImports = [ImportType.order, ImportType.inventory];
  const isUserInContextOfImportModal = history.location.search.includes(createImportSearchPath(summary.importType));

  return (
    <>
      {!hasWarnings && summary.successCount > 0 && (
        <Text formattedMessageValues={{ processed: summary.successCount }} textType={TextType.Paragraph} value={messages.processed} />
      )}
      {hasWarnings ? (
        <Text
          formattedMessageValues={{
            processed: summary.successCount,
            warningsCount: summary.warningsCount,
          }}
          textType={TextType.Paragraph}
          value={messages.processedWithWarnings}
        />
      ) : null}
      {hasErrors ? (
        <Text formattedMessageValues={{ failedCount: summary.failedCount }} textType={TextType.Paragraph} value={messages.failed} />
      ) : null}
      {/* no point of displaying link to the modal, when user is in the context of the modal */}
      {supportedSummaryImports.includes(summary.importType) && !isUserInContextOfImportModal && (
        <Button onClick={onViewSummaryClick} theme='link'>
          <FormattedMessage {...messages.viewImportSummary} />
        </Button>
      )}
    </>
  );
};

/**
 * @param {object} history
 * @param {Summary} summary
 * @param {() => void} onViewSummaryClick
 * @returns {void}
 */
const showSummaryNotification = (history, summary, onViewSummaryClick) => {
  if (summary.success) {
    const isNotFullySuccessful = summary.failedCount > 0 || summary.warningsCount > 0;
    NotificationHubService.showNotification(
      {
        notificationType: isNotFullySuccessful
          ? NotificationType.CSV_IMPORT_COMPLETED_WITH_WARNINGS
          : NotificationType.CSV_IMPORT_COMPLETED,
        defaultTitle: messages.jobCompletedTitle.defaultMessage,
        defaultDescription: `Your ${summary.importType} import finished successfully. ${summary.successCount + summary.failedCount} items imported. ${summary.successCount} succeeded and ${summary.failedCount} failed.`,
        metadataJson: JSON.stringify({
          importType: summary.importType,
          totalRecords: summary.successCount + summary.failedCount,
          processed: summary.successCount,
          failedCount: summary.failedCount,
          warningsCount: summary.warningsCount,
        }),
      },
      {
        title: <FormattedMessage {...messages.jobCompletedTitle} />,
        content: <SuccessfulImportContent history={history} onViewSummaryClick={onViewSummaryClick} summary={summary} />,
        dontHide: true,
        notificationType: isNotFullySuccessful ? NOTIFICATION_TYPE_WARNING : NOTIFICATION_TYPE_SUCCESS,
      },
    );
  } else {
    NotificationHubService.showNotification(
      {
        notificationType: NotificationType.CSV_IMPORT_FAILED,
        defaultTitle: messages.jobFailedTitle.defaultMessage,
        defaultDescription: `Your ${summary.importType} import failed unexpectedly. Please try again or contact support.`,
        metadataJson: JSON.stringify({ importType: summary.importType }),
      },
      {
        title: <FormattedMessage {...messages.jobFailedTitle} />,
        content: <Text textType={TextType.Paragraph} value={messages.jobFailedMessage} />,
        dontHide: true,
        notificationType: NOTIFICATION_TYPE_ERROR,
      },
    );
  }
};

const showProgressNotification = (onViewSummaryClick) => {
  return NotificationService.showNotification({
    title: <FormattedMessage {...messages.orderProgressNotificationTitle} />,
    content: (
      <Text
        formattedMessageValues={{
          a: (chunks) => (
            <Button onClick={onViewSummaryClick} theme='link'>
              {chunks}
            </Button>
          ),
        }}
        textType={TextType.Paragraph}
        value={messages.orderProgressNotificationBody}
      />
    ),
    notificationType: NOTIFICATION_TYPE_INFO,
    progress: true,
    dontHide: true,
  });
};

/**
 * @param {Observable} action$
 * @param {Observable<{getState: () => object, state: object}>} _
 * @param {{history: object}} deps
 * @returns {Observable}
 */
export const notifyAboutFinishedImport = (action$, _, { history }) => {
  return action$.pipe(
    ofType(Actions.IMPORT_PROCESS_REQUESTED),
    mergeMap((action) => {
      /** @type {RequestImportActionPayload} */
      const { payload } = action;
      return new Observable((observer) => {
        const onViewSummaryClick = () => {
          history.push({
            search: createImportSummarySearchPath(payload.importType, payload.jobId),
          });
        };

        let progressNotificationId = null;
        if (payload.importType === ImportType.order) {
          progressNotificationId = showProgressNotification(onViewSummaryClick);
        }

        const unbindFromEvent = PusherService.bindToEvent(payload.pusherEvent, (message) => {
          const summary = extractSummaryDetails(message, payload);
          if (payload.jobId !== summary.jobId) {
            return;
          }

          if (progressNotificationId) {
            NotificationService.removeNotification(progressNotificationId);
          }

          if (payload.customSummaryNotification) {
            const notificationFn = summary.success ? payload.customSummaryNotification.onSuccess : payload.customSummaryNotification.onFail;
            notificationFn();
          } else {
            // maybe we could specify message per import?
            // like instead "Import has been processed" do "Product import has been processed"
            showSummaryNotification(history, summary, onViewSummaryClick);
          }

          if (payload.workflowType) {
            trackWorkflow(
              payload.workflowType,
              summary.success ? WorkflowSteps.COMPLETE : WorkflowSteps.ERROR,
              normalizePusherMessageData(message?.data),
            );
          }

          observer.next(
            Actions.updateImportStatus({
              jobId: summary.jobId,
              importType: summary.importType,
              failedCount: summary.failedCount,
              successCount: summary.successCount,
              totalRecords: summary.totalRecords,
              warningsCount: summary.warningsCount,
              status: summary.success ? ImportStatus.COMPLETE : ImportStatus.FAILED,
            }),
          );

          csvImportFinishedSubject.next(summary.importType);
        });
        return unbindFromEvent;
      });
    }),
  );
};
